"use client"

import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useFormContext } from "react-hook-form"
import { cn } from "@/lib/utils"
import { formatDate } from "@/utils/helpers"
import clsx from "clsx"

type Props = {
  name: string
  label?: string
  type?:string
  dateLabel?: string
  timeLabel?: string
  required?: boolean
  disabled?: boolean
  defaultValue?: string | Date
  placeholderDate?: string
  placeholderTime?: string
  isfutureDateDisabled?: boolean
}

const RhfDateTimePicker = ({
  name,
  label,
  type= "dateTime",
  dateLabel = "Date",
  timeLabel = "Time",
  required = false,
  disabled = false,
  isfutureDateDisabled = false,
  defaultValue,
  placeholderDate = "Select date",
  placeholderTime,
}: Props) => {
  const { control } = useFormContext()

  const normalizeValue = (val: string | Date | undefined): string | undefined => {
    if (!val) return undefined
    const date = val instanceof Date ? val : new Date(val)
    if (isNaN(date.getTime())) return undefined
    return date.toISOString()
  }

  const getDateFromValue = (val: string | undefined): Date | undefined => {
    if (!val) return undefined
    // Extract date part (YYYY-MM-DD) to avoid timezone issues
    const datePart = val.split("T")[0]
    if (!datePart) return undefined
    const [year, month, day] = datePart.split("-").map(Number)
    if (!year || !month || !day) return undefined
    // Create date in local timezone to ensure calendar shows correct date
    const date = new Date(year, month - 1, day)
    return isNaN(date.getTime()) ? undefined : date
  }

  const getTimeFromValue = (val: string | undefined): string => {
    if (!val) return ""
    const date = new Date(val)
    if (isNaN(date.getTime())) return ""
    return date.toTimeString().slice(0, 5)
  }

  const getDatePart = (val: string | undefined): string => {
    if (!val) return ""
    return val.split("T")[0]
  }

  const getTimePart = (val: string | undefined): string => {
    if (!val) return ""
    const timeFull = val.split("T")[1]?.split(".")[0] || ""
    return timeFull.slice(0, 5)
  }

  return (
    <FormField
      control={control}
      name={name}
      defaultValue={defaultValue ? normalizeValue(defaultValue) : undefined}
      render={({ field }) => {
        const normalizedValue = normalizeValue(field.value)
        const currentDate = getDateFromValue(normalizedValue)
        const currentTime = getTimeFromValue(normalizedValue)
        const [open, setOpen] = React.useState(false)

        const handleDateSelect = (selectedDate: Date | undefined) => {
          setOpen(false)
          if (!selectedDate) {
            field.onChange(undefined)
            return
          }
          // Format date using local timezone to avoid date shifting
          const year = selectedDate.getFullYear()
          const month = String(selectedDate.getMonth() + 1).padStart(2, "0")
          const day = String(selectedDate.getDate()).padStart(2, "0")
          const datePart = `${year}-${month}-${day}`
          const timePart = currentTime ? `${currentTime}:00` : "00:00:00"
          const newValue = `${datePart}T${timePart}`
          field.onChange(newValue)
        }

        const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newTime = e.target.value
          if (newTime.length !== 5) return // Basic validation for HH:MM
          const datePart = getDatePart(normalizedValue)
          if (!datePart) return // Require date first
          const newValue = `${datePart}T${newTime}:00`
          field.onChange(newValue)
        }

        return (
          <FormItem className="flex flex-col gap-2 ">
            {label && (
              <FormLabel>
                {label}
                {required ? " *" : ""}
              </FormLabel>
            )}
            <FormControl>
              <div className="flex gap-4">
                {/* Date Picker - custom design: icon on right, same everywhere */}
                <div className={clsx( "flex flex-col gap-3 ", type === "dateTime" ? 'basis-1/2' : "basis-full")}>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        id={`${name}-date-picker`}
                        className={cn(
                          "w-full justify-between font-normal h-10 px-3 rounded-md border border-input bg-background text-left hover:bg-accent hover:text-accent-foreground",
                          !currentDate && "text-muted-foreground",
                          disabled && "opacity-50 cursor-not-allowed"
                        )}
                        disabled={disabled}
                      >
                        <span>{currentDate ? formatDate(currentDate) : placeholderDate}</span>
                        <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="rounded-lg border bg-popover p-3 shadow-md">
                        <Calendar
                          mode="single"
                          selected={currentDate}
                          defaultMonth={currentDate || new Date()}
                          captionLayout="dropdown"
                          onSelect={handleDateSelect}
                          disabled={disabled ? true : isfutureDateDisabled ? { after: new Date() } : false}
                          className="[--cell-size:2.5rem]"
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Picker */}
              {type === "dateTime" &&  <div className="flex flex-col gap-3 flex basis-1/2">
                 
                  <Input
                    type="time"
                    id={`${name}-time-picker`}
                    step="60" // Minutes only, adjust if seconds needed
                    value={currentTime}
                    onChange={handleTimeChange}
                    disabled={disabled || !currentDate} // Disable if no date selected
                    placeholder={placeholderTime}
                    className={cn(
                      "bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
                      disabled && "opacity-50 cursor-not-allowed"
                    )}
                  />
                </div>}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

export default RhfDateTimePicker